(ns manage.ad-variant.creation.feed.view
  (:require ["@mui/material/Button$default" :as Button]
            ["@mui/material/Chip$default" :as Chip]
            ["@mui/material/Divider$default" :as Divider]
            ["@mui/material/FormControlLabel$default" :as FormControlLabel]
            ["@mui/material/Icon$default" :as Icon]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/material/SvgIcon$default" :as SvgIcon]
            ["@mui/material/Switch$default" :as Switch]
            ["@mui/material/Tooltip$default" :as Tooltip]
            ["react-hook-form" :as rhf]
            ["src/svg/artwork/folder.svg$default" :as FolderArtwork]
            ["src/svg/editor/objects/link.svg$default" :as LinkIcon]
            ["src/svg/files_folders/file-spreadsheet.svg$default" :as FileSpreadsheet]
            ["src/svg/type/file_type/excel-csv.svg$default" :as CsvIcon]
            ["src/svg/type/file_type/json.svg$default" :as JsonIcon]
            ["src/svg/wpp/content-actions/edit.svg$default" :as Edit]
            [assemble.util :refer [file-extension]]
            [cljs-bean.core :refer [->clj ->js]]
            [clojure.string :as str]
            [common.patterns.empty-state-view :refer [empty-state-view]]
            [common.patterns.feed-import :as feed-patterns]
            [common.patterns.loading-view :refer [loading-view]]
            [common.queries :refer [use-query]]
            [common.resource.query.adset :as adset-queries]
            [common.url-template :as url-tpl]
            [common.util :as util]
            [common.validators.core :as validators]
            [manage.ad-variant.creation.feed.mapping.hooks :refer [use-mapping-form]]
            [manage.ad-variant.creation.feed.mapping.view :refer [feed-error-boundary mapping-view]]
            [manage.ad-variant.creation.feed.modal]
            [re-frame.core :as rf]
            [uix.core :as uix :refer [$ defui]]
            [uix.re-frame :refer [use-subscribe]]))

(def edit-feed-button
  (uix/as-react
   (fn [props]
     ($ Tooltip
        {:title "Edit feed"
         :placement "bottom"}
        ($ SvgIcon (util/spread-props props #js {:inheritViewBox true})
           ($ Edit))))))

(defui feed-info [{:keys [url name]}]
  (let [is-internal-url (some-> url validators/internal-url?)
        extension (or (file-extension name) (file-extension url))
        icon (cond
               (not is-internal-url) LinkIcon
               (= extension "csv") CsvIcon
               (= extension "json") JsonIcon
               :else FileSpreadsheet)]
    ($ Chip
       {:component "a"
        :clickable true
        :target "_blank"
        :size "medium"
        :href url
        :label (if is-internal-url
                 (or name (str "Download " (or (str/upper-case extension) "feed")))
                 url)
        :icon ($ SvgIcon {:component icon})
        :deleteIcon ($ edit-feed-button)
        :onDelete (fn [e]
                    (rf/dispatch [:modal/open {:id :ad-variant/import-feed}])
                    (.preventDefault e))
        :sx #js {:noWrap true
                 :maxWidth "400px"
                 ".MuiChip-label" #js {:color "primary.main"
                                       :fontSize "md"
                                       :textDecoration "underline"}
                 ".MuiChip-deleteIcon" #js {:color "text.subtle"}}})))

(defui dimension-mapping-toggle
  [{:keys [dimension-mapping-enabled? set-dimension-mapping-enabled? form-props on-submit*]}]
  (let [{:keys [setValue]} (->clj form-props)]
    ($ Stack {:direction "row" :alignItems "center"}
       ($ FormControlLabel
          {:label "Dimension mapping"
           :sx #js {:mr 0.5
                    ".MuiFormControlLabel-label" #js {:ml 1}}
           :control ($ Switch
                       {:checked dimension-mapping-enabled?
                        :size "small"
                        :onChange (fn [_]
                                    (let [new-val (not dimension-mapping-enabled?)]
                                      (set-dimension-mapping-enabled? new-val)
                                      (when (not new-val)
                                        (setValue "Dimensions.value" nil)
                                        (on-submit*))))})})
       ($ Tooltip {:title "Enable to map a feed column to template dimensions, supporting feeds with duplicate content IDs across different sizes."
                   :placement "top"}
          ($ Icon "info_outlined")))))

(defui content [{:keys [feed]}]
  (let [{:keys [form-props multiple-dimensions?] :as mapping-form-controls}
        (use-mapping-form {:feed-id (:id feed)})]
    ($ rhf/FormProvider (->js form-props)
       ($ Stack
          {:flex 1
           :gap 1
           :sx #js {:overflow "hidden"}}
          ($ Stack
             {:direction "row"
              :justifyContent "space-between"
              :alignItems "center"
              :py 1}
             ($ feed-info feed)
             ($ feed-patterns/import-status-with-refresh
                (when (not multiple-dimensions?)
                  ($ dimension-mapping-toggle mapping-form-controls))))
          ($ mapping-view {:feed feed
                           :mapping-form-controls mapping-form-controls})))))

(defui empty-view []
  (let [url-context (use-subscribe [:url-context])]
    ($ empty-state-view
       {:title "Import feed"
        :description "Effortlessly import content sheets with text, images, and media.
                    Let's upload and enhance your ads! 📄🔍"
        :artwork FolderArtwork
        :docs-href (url-tpl/render :docs/article (assoc url-context :slug "23852211701532-How-to-Create-Ad-Variants"))
        :actions ($ Button {:type "submit"
                            :variant "contained"
                            :onClick #(rf/dispatch [:modal/open {:id :ad-variant/import-feed}])}
                    "Import feed")})))

(defui feed-view []
  (let [{:keys [adset-id]} (use-subscribe [:url-context])
        {adset :data adset-state :status} (use-query (adset-queries/adset-query {:adset-id adset-id}))
        {feed :value} (use-subscribe [:resource.feed-importers/one])]
    (uix/use-effect (fn []
                      (when-let [feed-id (:feedId adset)]
                        (when (not= (:id feed) feed-id)
                          (rf/dispatch [:resource.feed-importers/get-one feed-id]))))
                    [adset feed])
    (cond
      (and (= adset-state "success") (nil? (:feedId adset))) ($ empty-view)
      (seq feed) ($ feed-error-boundary
                    ($ uix/suspense {:fallback ($ loading-view)}
                       ($ content {:feed feed})))
      :else ($ loading-view))))
